import {
  Grid,
  Label,
  SectionCard,
  SectionCardContent,
  SectionCardHeader,
  Separator,
  Stack,
} from '@eluve/components';
import {
  useAppointmentId,
  useTenantId,
} from '@eluve/frontend-appointment-hooks';
import { StartVirtualIntake } from '@eluve/frontend-feature-avatar';
import { OpenInAdminButton, OpenInReviewButton } from '@eluve/smart-blocks';

import { AppointmentFilesDrawer } from '../../AppointmentFilesDrawer';
import { UploadAudioFileButton } from '../UploadAudioFileButton';

import { AddToTranscript } from './AddToTranscript';
import { ChangeAppointmentOwner } from './ChangeAppointmentOwner';
import { LatestAppointmentTemplate } from './LatestAppointmentTemplate';
import { LatestTranscriptMetadata } from './LatestTranscriptMetadata';
import { RegeneratePatientOverview } from './RegeneratePatientOverview';
import { RegenerateSummary } from './RegenerateSummary';

interface EluveAdminPanelProps {
  readonlyMode?: boolean;
}
export const EluveAdminPanel: React.FC<EluveAdminPanelProps> = ({
  readonlyMode = false,
}) => {
  const appointmentId = useAppointmentId();
  const tenantId = useTenantId();

  return (
    <SectionCard>
      <SectionCardHeader>Eluve Admin Panel</SectionCardHeader>
      <SectionCardContent>
        {readonlyMode ? (
          <Stack
            align="start"
            gap={6}
            direction="horizontal"
            className="flex-wrap"
          >
            <Stack align="stretch" className="min-w-0 flex-1 basis-72" gap={2}>
              <LatestAppointmentTemplate appointmentId={appointmentId} />
              <LatestTranscriptMetadata
                appointmentId={appointmentId}
                tenantId={tenantId}
              />
            </Stack>
            <Stack align="stretch" className="min-w-0 flex-1 basis-72" gap={2}>
              <OpenInAdminButton
                appointmentId={appointmentId}
                tenantId={tenantId}
              />
              <OpenInReviewButton
                appointmentId={appointmentId}
                tenantId={tenantId}
              />
              <AppointmentFilesDrawer />
            </Stack>
          </Stack>
        ) : (
          <Stack
            align="start"
            gap={6}
            direction="horizontal"
            className="flex-wrap"
          >
            <Stack align="stretch" className="min-w-0 flex-1 basis-72" gap={2}>
              <AddToTranscript appointmentId={appointmentId} />
              <Separator className="my-2" />
              <Stack gap={2}>
                <Label className="px-1">Upload Audio File</Label>
                <UploadAudioFileButton appointmentId={appointmentId} />
              </Stack>
              <Separator className="my-2" />
              <LatestAppointmentTemplate appointmentId={appointmentId} />
              <LatestTranscriptMetadata
                appointmentId={appointmentId}
                tenantId={tenantId}
              />
            </Stack>
            <Stack align="stretch" className="min-w-0 flex-1 basis-72" gap={2}>
              <Label>Change Owner</Label>
              <ChangeAppointmentOwner
                appointmentId={appointmentId}
                tenantId={tenantId}
              />
              <Separator className="my-2" />
              <Label>Misc. Actions</Label>
              <Grid columns={2} gap={2}>
                <OpenInAdminButton
                  appointmentId={appointmentId}
                  tenantId={tenantId}
                />
                <OpenInReviewButton
                  appointmentId={appointmentId}
                  tenantId={tenantId}
                />
                <RegenerateSummary
                  appointmentId={appointmentId}
                  tenantId={tenantId}
                />
                <RegeneratePatientOverview
                  appointmentId={appointmentId}
                  tenantId={tenantId}
                />
                <StartVirtualIntake
                  appointmentId={appointmentId}
                  tenantId={tenantId}
                />
                <AppointmentFilesDrawer />
              </Grid>
            </Stack>
          </Stack>
        )}
      </SectionCardContent>
    </SectionCard>
  );
};
